import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type {
    AdminCompanyCreateResponse,
    AdminListParams,
    AdminProfessionalServiceRow,
    AdminCreditPricingConfigRow,
    AdminUserCreateResponse,
    AdminUserRow,
    AdminUserSyncResponse,
} from '../../services/api';
import {
    useAdminCompanies,
    useAdminCreditPricingConfigs,
    useAdminPricingConfigActiveSows,
    useAdminCreditSows,
    useAdminProfessionalServices,
    useCreditMutations,
    useAdminUserMutations,
    useAdminUsers,
} from '../../hooks/useApi';
import {
    ADMIN_SEARCH_DEBOUNCE_MS,
    buildAdminDashboardSearchParams,
    getAdminNewActionLabel,
    getDefaultAdminDashboardTab,
    parseAdminTabParam,
    shouldFetchAdminTab,
    type AdminDashboardTab,
} from './adminDashboardHelpers';
import { emptyAdminToast, type AdminToastState } from './adminToastTypes';
import type {
    ActivePricingUsageModalState,
    ActivePricingUsageTarget,
    AdminSowSortBy,
    CompanyDashboardRow,
    PricingConfigsView,
} from './adminDashboardTypes';

const PAGE_SIZE = 25;

export function useAdminDashboardState() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchDraft, setSearchDraft] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<AdminDashboardTab>(() =>
        getDefaultAdminDashboardTab(),
    );
    const [browseEnabledByTab, setBrowseEnabledByTab] = useState<
        Partial<Record<AdminDashboardTab, boolean>>
    >({});
    const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
    const [pricingConfigsView, setPricingConfigsView] = useState<PricingConfigsView>('all');
    const [selectedPricingConfig, setSelectedPricingConfig] =
        useState<AdminCreditPricingConfigRow | null>(null);
    const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false);
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [isProfessionalServiceDialogOpen, setIsProfessionalServiceDialogOpen] = useState(false);
    const [selectedProfessionalService, setSelectedProfessionalService] =
        useState<AdminProfessionalServiceRow | null>(null);
    const [professionalServiceDeleteTarget, setProfessionalServiceDeleteTarget] =
        useState<AdminProfessionalServiceRow | null>(null);
    const [selectedUser, setSelectedUser] = useState<AdminUserRow | null>(null);
    const [syncingUserId, setSyncingUserId] = useState<number | null>(null);
    const [toast, setToast] = useState<AdminToastState>(emptyAdminToast);
    const [activePricingUsageModal, setActivePricingUsageModal] =
        useState<ActivePricingUsageModalState>(null);
    const [archivePricingConfirmId, setArchivePricingConfirmId] = useState<string | null>(null);
    const [pageByTab, setPageByTab] = useState<Record<AdminDashboardTab, number>>({
        companies: 1,
        sows: 1,
        users: 1,
        pricing: 1,
        professionalServices: 1,
    });
    const [activeCompaniesOnlyFilter, setActiveCompaniesOnlyFilter] = useState(true);
    const [activeUsersOnlyFilter, setActiveUsersOnlyFilter] = useState(true);
    const [activeSowsOnlyFilter, setActiveSowsOnlyFilter] = useState(true);
    const [hideArchivedPricingFilter, setHideArchivedPricingFilter] = useState(true);
    const [sowSortBy, setSowSortBy] = useState<AdminSowSortBy>('end_date');

    const newActionLabel = getAdminNewActionLabel(activeTab);
    const activePage = pageByTab[activeTab];
    const query = searchQuery.trim() || undefined;
    const activeBrowseEnabled = browseEnabledByTab[activeTab] === true;
    const shouldFetchActiveTab = shouldFetchAdminTab(activeTab, query, activeBrowseEnabled);
    const sowsParams = useMemo(
        () => ({
            query,
            page: activePage,
            pageSize: PAGE_SIZE,
            sortBy: sowSortBy,
            ...(activeSowsOnlyFilter ? { activeSowsOnly: true } : {}),
        }),
        [activePage, query, sowSortBy, activeSowsOnlyFilter],
    );
    const companyListParams = useMemo(
        () => ({
            query,
            page: activePage,
            pageSize: PAGE_SIZE,
            ...(activeCompaniesOnlyFilter ? { activeCompaniesOnly: true } : {}),
        }),
        [activePage, query, activeCompaniesOnlyFilter],
    );
    const userListParams = useMemo(
        () => ({
            query,
            page: activePage,
            pageSize: PAGE_SIZE,
            ...(activeUsersOnlyFilter ? { activeUsersOnly: true } : {}),
        }),
        [activePage, query, activeUsersOnlyFilter],
    );
    const pricingListParams = useMemo(
        () => ({
            query,
            page: activePage,
            pageSize: PAGE_SIZE,
            ...(hideArchivedPricingFilter ? {} : { includeInactive: true }),
        }),
        [activePage, hideArchivedPricingFilter, query],
    );
    const professionalServicesListParams = useMemo<AdminListParams>(
        () => ({
            query,
            page: activePage,
            pageSize: PAGE_SIZE,
        }),
        [activePage, query],
    );
    const companiesQuery = useAdminCompanies(
        companyListParams,
        activeTab === 'companies' && shouldFetchActiveTab,
    );
    const sowsQuery = useAdminCreditSows(sowsParams, activeTab === 'sows');
    const usersQuery = useAdminUsers(userListParams, activeTab === 'users' && shouldFetchActiveTab);
    const pricingQuery = useAdminCreditPricingConfigs(pricingListParams, activeTab === 'pricing');
    const professionalServicesQuery = useAdminProfessionalServices(
        professionalServicesListParams,
        activeTab === 'professionalServices',
    );
    const activePricingUsageQuery = useAdminPricingConfigActiveSows(
        activePricingUsageModal?.target ?? null,
        activePricingUsageModal !== null,
    );
    const { syncUserToAuth0SSE, invalidateUserData } = useAdminUserMutations();
    const { deactivatePricingConfig } = useCreditMutations();

    const companyRows = useMemo<CompanyDashboardRow[]>(
        () => companiesQuery.data?.items ?? [],
        [companiesQuery.data?.items],
    );
    const sowRows = sowsQuery.data?.items ?? [];
    const users = usersQuery.data?.items ?? [];
    const pricingConfigs = pricingQuery.data?.items ?? [];
    const professionalServices = professionalServicesQuery.data?.items ?? [];
    const archivePricingConfirmRow = useMemo(() => {
        const items = pricingQuery.data?.items;
        if (!archivePricingConfirmId || !items) return undefined;
        return items.find((config) => config.id === archivePricingConfirmId);
    }, [archivePricingConfirmId, pricingQuery.data?.items]);

    const activeResult =
        activeTab === 'companies'
            ? companiesQuery
            : activeTab === 'sows'
              ? sowsQuery
              : activeTab === 'users'
                ? usersQuery
                : activeTab === 'pricing'
                  ? pricingQuery
                  : professionalServicesQuery;
    const activeData = activeResult.data;
    const isLoading = activeResult.isLoading;
    const error = activeResult.error;

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setSearchQuery(searchDraft);
            setPageByTab((current) => ({ ...current, [activeTab]: 1 }));
        }, ADMIN_SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [activeTab, searchDraft]);

    useEffect(() => {
        const tabFromUrl = parseAdminTabParam(searchParams.get('tab'));
        if (tabFromUrl) {
            setActiveTab(tabFromUrl);
        }
        const browseRaw = searchParams.get('browse');
        const browseOn = browseRaw === '1' || browseRaw === 'true';
        if (browseOn) {
            const tabForBrowse = tabFromUrl ?? 'companies';
            setBrowseEnabledByTab((current) => ({ ...current, [tabForBrowse]: true }));
        }
    }, [searchParams]);

    const openNewDialog = () => {
        if (activeTab === 'pricing') {
            setIsPricingDialogOpen(true);
            return;
        }
        if (activeTab === 'users') {
            setIsCreateUserModalOpen(true);
            return;
        }
        if (activeTab === 'companies') {
            setIsCreateCompanyModalOpen(true);
            return;
        }
        if (activeTab === 'professionalServices') {
            setSelectedProfessionalService(null);
            setIsProfessionalServiceDialogOpen(true);
        }
    };

    const setActivePage = (page: number) => {
        setPageByTab((current) => ({ ...current, [activeTab]: page }));
    };

    const syncAdminDashboardUrl = (
        tab: AdminDashboardTab,
        browseEnabled: Partial<Record<AdminDashboardTab, boolean>> = browseEnabledByTab,
    ) => {
        setSearchParams(buildAdminDashboardSearchParams(tab, browseEnabled), { replace: true });
    };

    const resetActiveTabPage = (tab: AdminDashboardTab) => {
        setActiveTab(tab);
        setSearchDraft('');
        setSearchQuery('');
        setPageByTab((current) => ({ ...current, [tab]: 1 }));
        syncAdminDashboardUrl(tab);
    };

    const handleSearchChange = (value: string) => {
        setSearchDraft(value);
    };

    const commitSearch = (value = searchDraft) => {
        setSearchQuery(value);
        setPageByTab((current) => ({ ...current, [activeTab]: 1 }));
    };

    const handleBrowseAll = () => {
        const nextBrowseEnabledByTab = { ...browseEnabledByTab, [activeTab]: true };
        setBrowseEnabledByTab(nextBrowseEnabledByTab);
        setPageByTab((current) => ({ ...current, [activeTab]: 1 }));
        syncAdminDashboardUrl(activeTab, nextBrowseEnabledByTab);
    };

    const handleActiveCompaniesOnlyChange = (checked: boolean) => {
        setActiveCompaniesOnlyFilter(checked);
        setPageByTab((current) => ({ ...current, [activeTab]: 1 }));
    };

    const handleActiveUsersOnlyChange = (checked: boolean) => {
        setActiveUsersOnlyFilter(checked);
        setPageByTab((current) => ({ ...current, [activeTab]: 1 }));
    };

    const handleActiveSowsOnlyChange = (checked: boolean) => {
        setActiveSowsOnlyFilter(checked);
        setPageByTab((current) => ({ ...current, sows: 1 }));
    };

    const handleSowSortByChange = (value: string) => {
        setSowSortBy(value as AdminSowSortBy);
        setPageByTab((current) => ({ ...current, sows: 1 }));
    };

    const handleHideArchivedPricingChange = (checked: boolean) => {
        setHideArchivedPricingFilter(checked);
        setPageByTab((current) => ({ ...current, pricing: 1 }));
    };

    const openActivePricingUsageModal = (
        target: ActivePricingUsageTarget,
        description: string,
        event?: { stopPropagation?: () => void },
    ) => {
        event?.stopPropagation?.();
        setActivePricingUsageModal({ target, description });
    };

    const showErrorToast = (message: string) => {
        setToast({ show: true, message, type: 'error' });
        window.setTimeout(() => setToast(emptyAdminToast), 4000);
    };

    const openArchivePricingConfirm = (
        pricingConfigId: string,
        event?: { stopPropagation?: () => void },
    ) => {
        event?.stopPropagation?.();
        setArchivePricingConfirmId(pricingConfigId);
    };

    const showSuccessToast = (message: string) => {
        setToast({ show: true, message, type: 'success' });
        window.setTimeout(() => setToast(emptyAdminToast), 3000);
    };

    const showWarningToast = (message: string) => {
        setToast({ show: true, message, type: 'warning' });
        window.setTimeout(() => setToast(emptyAdminToast), 5000);
    };

    const dismissToast = () => {
        setToast(emptyAdminToast);
    };

    const confirmArchivePricingConfig = async () => {
        if (archivePricingConfirmId === null) return;
        const id = archivePricingConfirmId;
        try {
            await deactivatePricingConfig.mutateAsync(id);
            setArchivePricingConfirmId(null);
            showSuccessToast('Pricing configuration archived.');
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Could not archive pricing configuration.';
            showErrorToast(message);
        }
    };

    const handleUserCreated = (result: AdminUserCreateResponse) => {
        showSuccessToast(`User created${result.isSso ? ' with SSO' : ''}.`);
    };

    const handleCompanyCreated = (result: AdminCompanyCreateResponse) => {
        const nextBrowseEnabledByTab = { ...browseEnabledByTab, companies: true };
        setBrowseEnabledByTab(nextBrowseEnabledByTab);
        setSearchDraft(result.name);
        setSearchQuery(result.name);
        setPageByTab((current) => ({ ...current, companies: 1 }));
        syncAdminDashboardUrl('companies', nextBrowseEnabledByTab);
        showSuccessToast('Company created.');
    };

    const handleSyncUser = async (userId: number) => {
        setSyncingUserId(userId);
        try {
            const result = await new Promise<AdminUserSyncResponse>((resolve, reject) => {
                void syncUserToAuth0SSE(
                    userId,
                    () => undefined,
                    reject,
                    (completedResult) => {
                        if (completedResult) {
                            resolve(completedResult);
                            return;
                        }
                        reject(new Error('User Auth0 sync finished without a result'));
                    },
                );
            });
            if (result.success) {
                showSuccessToast('User synced to Auth0.');
                await invalidateUserData();
                return;
            }

            showErrorToast(result.reason ?? 'Could not sync user to Auth0.');
        } catch (err) {
            showErrorToast(
                err instanceof Error && err.message ? err.message : 'Could not sync user to Auth0.',
            );
        } finally {
            setSyncingUserId(null);
        }
    };

    const handleCompanyRowClick = (company: CompanyDashboardRow) => {
        navigate(`/admin/companies/${company.companyId}`, {
            state: {
                adminCompanyDetail: {
                    companyId: String(company.companyId),
                    companyLabel: company.name,
                },
            },
        });
    };

    const handleSowRowClick = (sowId: string) => {
        navigate(`/admin/sows/${sowId}`);
    };

    const handleProfessionalServiceRowClick = (professionalServiceId: string) => {
        const professionalService = professionalServices.find(
            (item) => item.id === professionalServiceId,
        );
        if (!professionalService) return;
        setSelectedProfessionalService(professionalService);
        setIsProfessionalServiceDialogOpen(true);
    };

    const handleProfessionalServiceDelete = (professionalServiceId: string) => {
        const professionalService = professionalServices.find(
            (item) => item.id === professionalServiceId,
        );
        if (!professionalService) return;
        setProfessionalServiceDeleteTarget(professionalService);
    };

    return {
        activeData,
        activePage,
        activeCompaniesOnlyFilter,
        activePricingUsageModal,
        activePricingUsageQuery,
        activeSowsOnlyFilter,
        activeTab,
        archivePricingConfirmId,
        archivePricingConfirmRow,
        companyRows,
        commitSearch,
        confirmArchivePricingConfig,
        deactivatePricingConfig,
        dismissToast,
        error,
        handleActiveCompaniesOnlyChange,
        handleActiveSowsOnlyChange,
        handleActiveUsersOnlyChange,
        handleBrowseAll,
        handleCompanyCreated,
        handleCompanyRowClick,
        handleHideArchivedPricingChange,
        handleProfessionalServiceDelete,
        handleProfessionalServiceRowClick,
        handleSearchChange,
        handleSowRowClick,
        handleSowSortByChange,
        handleSyncUser,
        handleUserCreated,
        hideArchivedPricingFilter,
        isCreateCompanyModalOpen,
        isCreateUserModalOpen,
        isLoading,
        isPricingDialogOpen,
        isProfessionalServiceDialogOpen,
        newActionLabel,
        openActivePricingUsageModal,
        openArchivePricingConfirm,
        openNewDialog,
        pricingConfigs,
        professionalServices,
        professionalServiceDeleteTarget,
        pricingConfigsView,
        query,
        resetActiveTabPage,
        searchDraft,
        selectedPricingConfig,
        selectedProfessionalService,
        selectedUser,
        setActivePage,
        setActivePricingUsageModal,
        setArchivePricingConfirmId,
        setIsCreateCompanyModalOpen,
        setIsCreateUserModalOpen,
        setIsPricingDialogOpen,
        setIsProfessionalServiceDialogOpen,
        setPricingConfigsView,
        setSelectedPricingConfig,
        setSelectedProfessionalService,
        setProfessionalServiceDeleteTarget,
        setSelectedUser,
        shouldFetchActiveTab,
        showErrorToast,
        showSuccessToast,
        showWarningToast,
        sowRows,
        sowSortBy,
        syncingUserId,
        toast,
        activeUsersOnlyFilter,
        users,
    };
}

export { PAGE_SIZE };
